import React from 'react';
import { TrendingUp, Wallet, Hexagon } from 'lucide-react';
import { getCategoryColor } from '../../utils/helpers';
import { iconMap, HoldingSparkline } from './helpers';

export const HoldingsTab = ({ holdings, performance, formatCurrency }) => {
  return (
    <div>
      {performance && performance.holdings && performance.holdings.length > 0 && (
        <div className="luxury-card p-6 mb-6" data-testid="holdings-performance-summary">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={14} className="text-gold" />
            <span className="text-xs text-gold uppercase tracking-wider">Portfolio Performance</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Invested</p>
              <p className="text-white text-lg font-semibold">{formatCurrency(performance.total_invested)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Current Value</p>
              <p className="text-gold text-lg font-semibold">{formatCurrency(performance.total_current_value)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">P&L</p>
              <p className={`text-lg font-semibold ${performance.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {performance.total_pnl >= 0 ? '+' : ''}{formatCurrency(performance.total_pnl)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Return</p>
              <p className={`text-lg font-semibold ${performance.total_pnl_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {performance.total_pnl_percent >= 0 ? '+' : ''}{performance.total_pnl_percent.toFixed(2)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {holdings.length === 0 ? (
        <div className="luxury-card p-12 text-center">
          <Wallet size={48} className="text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-white font-serif text-xl mb-2">No Holdings</h3>
          <p className="text-muted-foreground text-sm">Purchase fractions on the marketplace to start building your portfolio.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {holdings.map((h) => {
            const Icon = iconMap[h.category] || Hexagon;
            const perf = performance?.holdings?.find(p => p.token_id === h.token_id);
            const pnl = perf?.pnl ?? 0;
            const pnlPct = perf?.pnl_percent ?? 0;
            const positive = pnl >= 0;
            return (
              <div key={h.token_id} className="luxury-card p-5" data-testid={`holding-${h.token_id}`}>
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                    {h.image_url ? (<img src={h.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${h.image_url}` : h.image_url} alt="" className="w-full h-full object-cover rounded" />) : (<Icon size={24} className={getCategoryColor(h.category)} />)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{h.asset_name}</h3>
                    <p className="text-xs text-muted-foreground">{h.fractions_owned.toLocaleString()} fractions · {h.ownership_percentage}% ownership</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gold font-semibold">{formatCurrency(h.total_value)}</p>
                    {perf && (
                      <p className={`text-xs font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`pnl-${h.token_id}`}>
                        {positive ? '+' : ''}{formatCurrency(pnl)} ({positive ? '+' : ''}{pnlPct.toFixed(2)}%)
                      </p>
                    )}
                  </div>
                </div>
                {perf && perf.timeline && perf.timeline.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#1A1A1A]">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Value since first buy</p>
                      <p className="text-[10px] text-muted-foreground">Avg buy: {formatCurrency(perf.average_buy_price)} · Now: {formatCurrency(perf.current_price)}</p>
                    </div>
                    <HoldingSparkline data={perf.timeline} positive={positive} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
